import HeaderRow from "./HeaderRow";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { ScrollSync } from "react-scroll-sync";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from "@dnd-kit/modifiers";
import { useMoveOperation } from "@/lib/queries/hooks";
import FooterRow from "./FooterRow";
import fractionalIndex from "fractional-index";
import SubpartList from "./SubpartList";
import SwitchOperation from "./SwitchOperation";
import AddOperation from "./lib/AddOperation";
import OperationListContext from "./lib/OperationListContext";
import OperationContext from "./lib/OperationContext";
import { components } from "@/lib/ApiSchema";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import useUpdateOperation from "@/lib/queries/useUpdateOperation";
import useScrollToBottomOnAppendOperation from "./lib/useScrollToBottomOnAppendOperation";

function getFractionalIndex(
  leftOrdinal: string | null,
  rightOrdinal: string | null,
) {
  try {
    return fractionalIndex(leftOrdinal, rightOrdinal);
  } catch (error) {
    console.error("Error calculating fractional index:", error);
    // Fallback: return a random string to avoid breaking rendering
    return Math.random().toString();
  }
}

export default function OperationList({
  part,
}: {
  part: components["schemas"]["RequestedPartWithOperationsSchema"];
}) {
  const { requested_quantities: quantities, will_bid, operations } = part;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const { moveOperation } = useMoveOperation({});
  const updateOperation = useUpdateOperation();

  const canEditQuoteRevision = useGetCurrentQuoteRevision()?.can_edit;

  const operationListContainerRef =
    useScrollToBottomOnAppendOperation(operations);

  return (
    <OperationListContext.Provider value={{ quantities }}>
      <ScrollSync>
        <div
          className={`relative w-full grow flex flex-col ${will_bid === null ? "overflow-y-auto" : "overflow-y-hidden"}`}
        >
          <HeaderRow preventEdit={!!part.parent} />
          {part.children.length > 0 && <SubpartList subparts={part.children} />}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[
              restrictToVerticalAxis,
              restrictToFirstScrollableAncestor,
            ]}
          >
            <div
              className="w-full flex flex-col"
              ref={operationListContainerRef}
            >
              <SortableContext
                items={operations}
                strategy={verticalListSortingStrategy}
              >
                {operations.map((data, i) => (
                  <div key={data.id} className="flex flex-col">
                    {canEditQuoteRevision && i < operations.length && (
                      <div className="flex justify-start group/add-operation hover:bg-blue-100 h-2 relative">
                        <AddOperation
                          key={data.id + i}
                          ordinal={getFractionalIndex(
                            operations[i - 1]?.ordinal ?? null,
                            operations[i]?.ordinal ?? null,
                          )}
                        />
                      </div>
                    )}
                    <OperationContext.Provider
                      value={{
                        operationData: data,
                        allowActions: true,
                        updateOperation,
                      }}
                      key={data.id}
                    >
                      <SwitchOperation type={data.type} />
                    </OperationContext.Provider>
                  </div>
                ))}
              </SortableContext>
            </div>
          </DndContext>
          <FooterRow
            operations={operations.concat(
              part.children.flatMap((c) => c.operations),
            )}
            left={
              canEditQuoteRevision ? (
                <AddOperation
                  last
                  ordinal={getFractionalIndex(
                    operations[operations.length - 1]?.ordinal ?? null,
                    null,
                  )}
                />
              ) : (
                <div />
              )
            }
          />
          {will_bid !== null && (
            <div
              className={`absolute top-0 right-0 left-0 h-[9999px] ${will_bid ? "bg-green-500/10" : "bg-red-500/10"} z-10 cursor-not-allowed`}
            />
          )}
        </div>
      </ScrollSync>
    </OperationListContext.Provider>
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (operations && active.id !== over?.id) {
      const oldIndex = active.data.current?.sortable.index;
      const newIndex = over?.data.current?.sortable.index;
      const reorderedOperations = arrayMove(operations, oldIndex, newIndex);
      const leftOrdinal =
        newIndex === 0 ? null : reorderedOperations[newIndex - 1].ordinal;
      const rightOrdinal =
        newIndex === reorderedOperations.length - 1
          ? null
          : reorderedOperations[newIndex + 1].ordinal;
      const newOrdinal = getFractionalIndex(leftOrdinal, rightOrdinal);
      moveOperation({ id: active.id.toString(), ordinal: newOrdinal });
    }
  }
}
